// THE LOCATION PICKER — one server-driven search behind every screen that asks
// a human to name a bin (spec 06 §4.3/§4.5/§4.8, §5 row 1).
//
// WHY IT IS NOT A `<select>` OF A FETCHED PAGE. The list endpoint defaults to 50
// rows and caps at 200. Staging's extrusion plant already holds more than 200
// locations and a real factory's bin tree is thousands, so every picker that
// rendered "one page, filtered in the browser" was OFFERING A SUBSET IT COULD
// NOT NAME: the destination picker on the unit card would not show a storekeeper
// the bin she was standing in, and no message anywhere said why. That is the
// M06-S02 truncation class on a WRITE path.
//
// The shape is module 01's exact-SKU pull-up, generalised: the needle must be
// reachable regardless of which page it would fall on, so the search runs on the
// SERVER (`q` matches code, name and materialized path) and the screen renders
// what the server found. Three properties this component owes its callers:
//
//   1. TYPING SEARCHES THE TENANT, not the fetched page. Debounced, so a
//      12-character bin code is one request rather than twelve.
//   2. THE CHOSEN LOCATION SURVIVES THE SEARCH. Its path is resolved by id
//      (`useLocationPathIndex`), never by hoping it is in the current result
//      page — a picker that silently dropped the already-chosen bin when the
//      user typed would be a worse bug than the one this fixes.
//   3. AN INCOMPLETE ANSWER SAYS SO. When the server reports more matches than
//      the page carries, the note tells the user to narrow — never a silent cut
//      (§3.8, D-060: no bound is silent).
//
// `kinds` filters the RESULT of the server search rather than the query: the
// list endpoint takes one `kind`, the destination rule is "bin or zone", and the
// kinds this removes (warehouses, the 8 virtual locations) are a handful per
// facility — bounded by construction, so filtering them here cannot hide a
// needle the way filtering a truncated page did.
//
// `serverKinds` is the opposite case (task #253, D-330): the kinds the caller
// may use are themselves numerous, as a bin's legal parents are every zone AND
// every warehouse, so each is narrowed on the SERVER with a page of its own
// (`useLocationSearchByKinds`). A client filter there would re-open the
// truncation class, and a shared page would open on twenty warehouses.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import {
  useLocationPathIndex,
  useLocationSearchByKinds,
  type InventoryLocation,
} from "../hooks/inventory-queries";

export interface LocationPickerProps {
  /** The chosen location id, or "" for none. */
  value: string;
  onChange: (next: string) => void;
  /** Field label — every caller names its own act ("Destination", "Location"). */
  label: string;
  /** Narrow to one facility; omit for a tenant-wide picker (the ledger filter). */
  facilityId?: string | undefined;
  /** ONE kind, narrowed on the SERVER (§5 row 1 `kind`) — the zone's parent
   * picker, where the legal parent is exactly `warehouse`. */
  kind?: string | undefined;
  /** SEVERAL kinds, each narrowed on the server with a page of its own — the
   * bin's parent picker (zone or warehouse, §2.1). Takes precedence over
   * `kind`; see the header. */
  serverKinds?: readonly string[] | undefined;
  /** Only locations that accept new movements. */
  isActive?: boolean | undefined;
  /** Only locations a cycle count may scope over (§5 row 35). */
  isCountable?: boolean | undefined;
  /** Kinds the caller can actually use; see the header. */
  kinds?: readonly string[] | undefined;
  /** Text of the button that un-picks the current choice. A FILTER says "all
   * locations"; a destination says "change". */
  clearLabel?: string | undefined;
  /** What "there are none at all" MEANS here — "no countable location in this
   * facility yet" says more than a generic prompt, and an empty picker with
   * nothing typed is a real state a screen should be able to name. */
  emptyLabel?: string | undefined;
  disabled?: boolean | undefined;
  /** Prefix for the `data-testid`s: `<id>-input`, `-results`, `-chosen`, `-clear`. */
  testId: string;
}

export function LocationPicker({
  value,
  onChange,
  label,
  facilityId,
  kind,
  serverKinds,
  isActive,
  isCountable,
  kinds,
  clearLabel,
  emptyLabel,
  disabled,
  testId,
}: LocationPickerProps) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  // Rev-7 (UA-inventory phase 2): the suggestions open only while the box has
  // focus and float over the page. Rendered at rest, the first page of the
  // tenant's bins pushed the movements filter row apart and painted over the
  // fields below it (the audit's measured overlap, finding 8).
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  // "Change" un-picks the choice to pick another, so the box it reveals takes
  // focus and offers its suggestions straight away.
  const focusAfterClear = useRef(false);
  useEffect(() => {
    if (value.length === 0 && focusAfterClear.current) {
      focusAfterClear.current = false;
      input.current?.focus();
      setOpen(true);
    }
  }, [value]);
  const debounced = useDebouncedValue(text, 250);

  const search = useLocationSearchByKinds({
    q: debounced,
    facilityId,
    kinds: serverKinds ?? (kind !== undefined && kind.length > 0 ? [kind] : []),
    isActive,
    isCountable,
  });

  // The CHOSEN row, resolved by id — the only way its path is guaranteed to
  // render while the result page shows something else entirely.
  const chosenIndex = useLocationPathIndex(value.length > 0 ? [value] : []);
  const chosen = chosenIndex.byId.get(value);

  const matches = search.items.filter(
    (location: InventoryLocation) => kinds === undefined || kinds.includes(location.kind),
  );
  // The server said there is more behind a page. `kinds` may also have
  // removed rows locally, which is equally a reason to say "narrow it".
  const more = search.more;
  // Nothing typed, the search settled, and nothing at all to offer: a real
  // state the caller names ("no countable location in this facility yet").
  // It stands under the box at rest too, because closed suggestions would
  // otherwise leave an empty box with no reason beside it.
  const settledEmpty =
    !search.isPending &&
    search.error === null &&
    matches.length === 0 &&
    debounced.trim().length === 0 &&
    text.trim().length === 0;
  const restingEmptyNote =
    !open && settledEmpty && emptyLabel !== undefined ? (
      <p className="inv-hint" data-testid={`${testId}-empty`}>
        {emptyLabel}
      </p>
    ) : null;

  if (value.length > 0) {
    return (
      <div className="inv-label location-picker" data-testid={testId}>
        <span className="filter-chip-label">{label}</span>
        <span className="location-picker-chosen" data-testid={`${testId}-chosen`}>
          {chosen === undefined
            ? chosenIndex.isPending
              ? t("inventory.locationPicker.resolving")
              : /* An id that no longer resolves (deactivated, or outside this
                   caller's facility scope) is shown AS an id rather than as a
                   blank — the user has to be able to see what is set. */
                value
            : `${chosen.path} — ${chosen.name}`}
        </span>
        {disabled === true ? null : (
          <button
            type="button"
            className="ds-lnk"
            data-testid={`${testId}-clear`}
            onClick={() => {
              focusAfterClear.current = true;
              onChange("");
              setText("");
            }}
          >
            {clearLabel ?? t("inventory.locationPicker.change")}
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      ref={root}
      className="inv-label location-picker"
      data-testid={testId}
      data-open={open ? "true" : "false"}
      onFocus={() => {
        setOpen(true);
      }}
      onBlur={(event) => {
        const next = event.relatedTarget;
        if (next instanceof Node && root.current?.contains(next) === true) return;
        setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          // Handled here: a FilterBar panel around the picker leaves this
          // Escape alone and closes on the next one.
          event.preventDefault();
          setOpen(false);
        }
      }}
    >
      <span className="filter-chip-label">{label}</span>
      <input
        ref={input}
        className="ds-input"
        value={text}
        disabled={disabled}
        aria-label={label}
        data-testid={`${testId}-input`}
        placeholder={t("inventory.locationPicker.placeholder")}
        onChange={(event) => {
          setText(event.target.value);
          setOpen(true);
        }}
      />
      {open ? (
        <div className="location-picker-pop">
          {search.isPending ? (
            <p className="inv-hint" role="status">
              {t("inventory.locationPicker.searching")}
            </p>
          ) : null}
          {search.error !== null ? (
            <p className="form-error" role="alert">
              {t("inventory.locationPicker.failed")}
            </p>
          ) : null}
          {!search.isPending && search.error === null && matches.length === 0 ? (
            <p className="inv-hint" data-testid={`${testId}-empty`}>
              {debounced.trim().length > 0
                ? t("inventory.locationPicker.noMatch", { q: debounced.trim() })
                : (emptyLabel ?? t("inventory.locationPicker.typeToSearch"))}
            </p>
          ) : null}
          {matches.length > 0 ? (
            <ul className="picker-results" data-testid={`${testId}-results`}>
              {matches.map((location) => (
                <li key={location.id}>
                  <button
                    type="button"
                    // Keep focus in the box while the press lands, so the list
                    // does not close under the pointer on a browser that never
                    // focuses a pressed button.
                    onMouseDown={(event) => {
                      event.preventDefault();
                    }}
                    onClick={() => {
                      onChange(location.id);
                      setOpen(false);
                    }}
                  >
                    {location.path} — {location.name}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {/* §3.8 / D-060: the cut is stated, above the results, never inferred. */}
          {more ? (
            <p className="degraded-note is-warning" role="note" data-testid={`${testId}-more`}>
              {t("inventory.locationPicker.more", { n: matches.length })}
            </p>
          ) : null}
        </div>
      ) : null}
      {restingEmptyNote}
    </div>
  );
}
